import type { ReactNode } from 'react'
import {
  IdentificationIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  BanknotesIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline'
import { formatPriceSimple } from '@/utils/courseFormat'
import {
  formatDateVi,
  registerStatusLabel,
  StudentCourseDetailData,
} from './types'

interface ClassRegistrationInfoProps {
  classroomInfo: NonNullable<StudentCourseDetailData['classroomInfo']> | null
  registrationInfo: StudentCourseDetailData['registrationInfo']
  studentRegisteredAt?: string
  method: string
  expireDate?: string | null
}

function Field({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-m2-primary/15 text-m2-primary mt-0.5">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm text-m2-on-surface-variant">{label}</p>
        <p className="text-base font-semibold text-m2-on-surface break-words">{value}</p>
      </div>
    </div>
  )
}

const ClassRegistrationInfo = ({
  classroomInfo,
  registrationInfo,
  studentRegisteredAt,
  method,
  expireDate,
}: ClassRegistrationInfoProps) => {
  const status = registerStatusLabel(registrationInfo?.status)
  const orgFee =
    registrationInfo?.orgFee != null
      ? formatPriceSimple(registrationInfo.orgFee)
      : 'Chưa có thông tin'
  const paidFee =
    registrationInfo?.fee != null
      ? formatPriceSimple(registrationInfo.fee)
      : 'Chưa có thông tin'

  if (!classroomInfo && method === 'VIDEO') {
    return (
      <div className="bg-m2-surface-container border border-m2-outline-variant rounded-2xl p-6 shadow-sm h-full">
        <h3 className="text-xl font-bold text-m2-on-surface mb-5 border-b border-m2-outline-variant pb-3">
          Thông tin đăng ký
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field
            icon={<BanknotesIcon className="h-4 w-4" />}
            label="Học phí gốc"
            value={orgFee}
          />
          <Field
            icon={<BanknotesIcon className="h-4 w-4" />}
            label="Học phí đã đóng"
            value={paidFee}
          />
          <Field
            icon={<CalendarIcon className="h-4 w-4" />}
            label="Ngày đăng ký"
            value={formatDateVi(registrationInfo?.registeredAt || studentRegisteredAt)}
          />
          <Field
            icon={<CheckBadgeIcon className="h-4 w-4" />}
            label="Ngày xác nhận"
            value={formatDateVi(registrationInfo?.approvedAt)}
          />
          <Field
            icon={<IdentificationIcon className="h-4 w-4" />}
            label="Trạng thái"
            value={status}
          />
          <Field
            icon={<ClockIcon className="h-4 w-4" />}
            label="Thời hạn học"
            value={expireDate ? `Hết hạn: ${formatDateVi(expireDate)}` : 'Không giới hạn'}
          />
        </div>
      </div>
    )
  }

  if (!classroomInfo) {
    return (
      <div className="bg-m2-surface-container border border-m2-outline-variant rounded-2xl p-6 shadow-sm h-full">
        <h3 className="text-xl font-bold text-m2-on-surface mb-4">Trạng thái</h3>
        <div className="rounded-xl border border-m2-primary/30 bg-m2-primary/10 p-4 text-m2-on-surface">
          <p className="font-semibold mb-1">Đang chờ xếp lớp</p>
          <p className="text-sm text-m2-on-surface-variant">
            Bạn đã đăng ký thành công. Vui lòng chờ Trung tâm liên hệ và xếp lớp.
          </p>
        </div>
        {(registrationInfo || studentRegisteredAt) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
            <Field
              icon={<BanknotesIcon className="h-4 w-4" />}
              label="Học phí gốc"
              value={orgFee}
            />
            <Field
              icon={<BanknotesIcon className="h-4 w-4" />}
              label="Học phí đã đóng"
              value={paidFee}
            />
            <Field
              icon={<CalendarIcon className="h-4 w-4" />}
              label="Ngày đăng ký"
              value={formatDateVi(registrationInfo?.registeredAt || studentRegisteredAt)}
            />
            <Field
              icon={<CheckBadgeIcon className="h-4 w-4" />}
              label="Ngày xác nhận"
              value={formatDateVi(registrationInfo?.approvedAt)}
            />
            <Field
              icon={<IdentificationIcon className="h-4 w-4" />}
              label="Trạng thái"
              value={status}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-m2-surface-container border border-m2-outline-variant rounded-2xl p-6 shadow-sm h-full">
      <h3 className="text-xl font-bold text-m2-on-surface mb-5 border-b border-m2-outline-variant pb-3">
        Thông tin lớp & đăng ký
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field
          icon={<IdentificationIcon className="h-4 w-4" />}
          label="Tên lớp"
          value={classroomInfo.name}
        />
        <Field
          icon={<CalendarIcon className="h-4 w-4" />}
          label="Ngày khai giảng"
          value={formatDateVi(classroomInfo.startDate)}
        />
        <Field
          icon={<BanknotesIcon className="h-4 w-4" />}
          label="Học phí gốc"
          value={orgFee}
        />
        <Field
          icon={<BanknotesIcon className="h-4 w-4" />}
          label="Học phí đã đóng"
          value={paidFee}
        />
        <Field
          icon={<CalendarIcon className="h-4 w-4" />}
          label="Ngày đăng ký"
          value={formatDateVi(registrationInfo?.registeredAt || studentRegisteredAt)}
        />
        <Field
          icon={<CheckBadgeIcon className="h-4 w-4" />}
          label="Ngày xác nhận"
          value={formatDateVi(registrationInfo?.approvedAt)}
        />
        <Field
          icon={<IdentificationIcon className="h-4 w-4" />}
          label="Trạng thái"
          value={status}
        />
        <Field
          icon={<ClockIcon className="h-4 w-4" />}
          label="Lịch học"
          value={classroomInfo.schedule || 'Chưa cập nhật'}
        />
        {method === 'OFFLINE' && (
          <Field
            icon={<MapPinIcon className="h-4 w-4" />}
            label="Địa điểm"
            value={classroomInfo.address || 'Chưa cập nhật'}
          />
        )}
      </div>
    </div>
  )
}

export default ClassRegistrationInfo
